import React, { useState, useEffect } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { paths } from "utils/constants";
import { onUserLogout } from "store/asyncThunk";
import { selectAutomationAllowed, toggleRealDemo } from "store/slices/main";
import useMediaQuery from "hooks/useMediaQuery";
import ProtectedContent from "components/UI/ProtectedContent";
import Logo from "components/UI/Logo/Logo";
import ProfileAvatar from "components/UI/ProfileAvatar";

import { Dropdown } from "antd";
import "./ModernHeader.scss";

const userMenuItems = [
  {
    label: "Settings",
    key: "settings",
  },
  {
    label: "Logout",
    key: "0",
  },
];

const ModernHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 700px)");

  const user = useSelector((state) => state.main.user);
  const accountType = useSelector((state) => state.main.accountType);
  const automationAllowed = useSelector(selectAutomationAllowed);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroups, setOpenMobileGroups] = useState({});

  // Ordered by relevance: daily trading first, research tools next,
  // manager-only tools (hidden for regular users) last.
  const navigationItems = [
    { name: "Dashboard", path: paths.home },
    { name: "Trading", path: paths.trading },
    // the user's AutoTrader: setup, health, controls
    ...(automationAllowed ? [{ name: "Automation", path: paths.automation }] : []),
    { name: "AI Backtests", path: paths.ai },
    { name: "Strategies Explorer", path: paths.strategiesExplorer },
    {
      name: "Options",
      children: [
        { name: "Options Analysis", path: paths.optionsAnalysis },
        { name: "Options Leaderboard", path: paths.optionsLeaderboard },
        {
          name: "Options Simulator",
          path: paths.optionSimulator,
          protected: true,
          requiredPermissions: ["manager"],
        },
      ],
    },
    {
      name: "Agent",
      path: paths.agent,
      protected: true,
      requiredPermissions: ["manager"],
    },
    {
      name: "Create Model",
      path: paths.createModel,
      protected: true,
      requiredPermissions: ["manager"],
    },
    {
      name: "Admin panel",
      path: paths.admin,
      protected: true,
      requiredPermissions: ["manager"],
    },
  ];

  const handleLogout = () => {
    dispatch(onUserLogout());
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    setIsMobileMenuOpen(false);
  };

  const handleToggleRealDemo = () => {
    dispatch(toggleRealDemo());
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleMobileGroup = (name) => {
    setOpenMobileGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleOnClick = (e) => {
    if (e.key === "0") {
      handleLogout();
    } else if (e.key === "settings") {
      navigate(paths.userSettings);
    }
  };

  const wrapIfProtected = (item, node) => {
    if (!item.protected) return node;
    return (
      <ProtectedContent
        key={item.path}
        requiredPermissions={item.requiredPermissions}
      >
        {node}
      </ProtectedContent>
    );
  };

  const renderNavigationItem = (item) => {
    if (item.children) {
      return isMobile ? renderMobileGroup(item) : renderDesktopGroup(item);
    }

    // Handle conditional items
    if (item.conditional && !item.condition) {
      return null;
    }

    const navButton = (
      <NavLink
        key={item.path}
        to={item.path}
        className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
        onClick={() => handleNavigation(item.path)}
      >
        <span className="nav-text">{item.name}</span>
      </NavLink>
    );

    const mobileNavButton = (
      <NavLink
        key={item.path}
        to={item.path}
        className={({ isActive }) =>
          `mobile-nav-item ${isActive ? "active" : ""}`
        }
        onClick={() => handleNavigation(item.path)}
      >
        <span className="mobile-nav-text">{item.name}</span>
      </NavLink>
    );

    return wrapIfProtected(item, isMobile ? mobileNavButton : navButton);
  };

  const renderDesktopGroup = (group) => {
    const isGroupActive = group.children.some(
      (child) => location.pathname === child.path
    );

    return (
      <div
        key={group.name}
        className={`nav-item nav-dropdown ${isGroupActive ? "active" : ""}`}
      >
        <span className="nav-text">{group.name}</span>
        <ChevronDownIcon className="w-4 h-4 nav-caret" />
        <div className="nav-dropdown-menu">
          {group.children.map((child) =>
            wrapIfProtected(
              child,
              <NavLink
                key={child.path}
                to={child.path}
                className={({ isActive }) =>
                  `nav-dropdown-item ${isActive ? "active" : ""}`
                }
                onClick={() => handleNavigation(child.path)}
              >
                {child.name}
              </NavLink>
            )
          )}
        </div>
      </div>
    );
  };

  const renderMobileGroup = (group) => {
    const isOpen = !!openMobileGroups[group.name];

    return (
      <div key={group.name} className="mobile-nav-group">
        <button
          type="button"
          className={`mobile-nav-item mobile-nav-group-toggle ${
            isOpen ? "open" : ""
          }`}
          onClick={() => toggleMobileGroup(group.name)}
        >
          <span className="mobile-nav-text">{group.name}</span>
          <ChevronDownIcon className="w-4 h-4 mobile-nav-caret" />
        </button>
        {isOpen && (
          <div className="mobile-nav-subgroup">
            {group.children.map((child) =>
              wrapIfProtected(
                child,
                <NavLink
                  key={child.path}
                  to={child.path}
                  className={({ isActive }) =>
                    `mobile-nav-item mobile-nav-subitem ${
                      isActive ? "active" : ""
                    }`
                  }
                  onClick={() => handleNavigation(child.path)}
                >
                  <span className="mobile-nav-text">{child.name}</span>
                </NavLink>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to restore body scroll when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="modern-header">
      <div className="header-container">
        {/* Left Section - Logo and Desktop Navigation */}
        <div className="header-left">
          <div className="lg:block hidden">
            <Logo />
          </div>
          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navigationItems.map(renderNavigationItem)}
          </nav>
        </div>

        {/* Right Section - Controls and User */}
        <div className="header-right">
          {/* Account Type Switcher */}
          <div className="account-switcher">
            <button
              className={`account-btn ${
                accountType === "demo" ? "active" : ""
              }`}
              onClick={() => handleToggleRealDemo()}
            >
              Demo
            </button>
            <button
              className={`account-btn ${
                accountType === "real" ? "active" : ""
              }`}
              onClick={() => handleToggleRealDemo()}
            >
              Live
            </button>
          </div>

          {/* User Profile */}
          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleOnClick,
            }}
            trigger={["click"]}
          >
            <div className="user-profile">
              <ProfileAvatar user={user} showTooltip={false} />
              <span className="user-name">Hello, {user?.firstName}</span>
            </div>
          </Dropdown>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-content">
          {/* Mobile Navigation */}
          <nav className="mobile-nav">
            {navigationItems.map(renderNavigationItem)}
          </nav>

          {/* Mobile User Actions */}
          <div className="mobile-user-actions">
            <button
              className="mobile-action-btn"
              onClick={() => navigate(paths.userSettings)}
            >
              <Cog6ToothIcon className="w-5 h-5" />
              <span>Settings</span>
            </button>
            <button
              className="mobile-action-btn mobile-action-btn--logout"
              onClick={handleLogout}
            >
              <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={toggleMobileMenu} />
      )}
    </header>
  );
};

export default ModernHeader;
